<script lang="ts">
  import { Link } from '@inertiajs/svelte';
  import type {
    Project,
    ProjectsByStatus,
    Task,
    TaskPriority,
    ProjectCategory,
  } from '../lib/types';
  import { categoryIcons, getWeekDisplayRangeWithYear, getMonthDisplayName } from '../lib/utils';
  import { openProjectRail, persistedWritable } from '../lib/stores/ui';
  import {
    batchFetchProjectTasks,
    subscribeToAllCachedTasks,
    setProjectTasks,
  } from '../lib/stores/projectTasksCache';
  import {
    completeTask,
    toggleTaskActive,
    addTask,
    deleteTask as deleteTaskApi,
    markTaskWontDo,
  } from '../lib/taskApi';
  import { jsonHeaders } from '../lib/apiHelpers';
  import { computePosition, flip, shift, offset } from '@floating-ui/dom';
  import TaskDetailPopover from './TaskDetailPopover.svelte';
  import MobileTaskList from './mobile/MobileTaskList.svelte';
  import type { NewTaskDraft } from './mobile/MobileAddTask.svelte';
  import { viewport } from '../lib/stores/viewport';
  import {
    GROUP_BY_STORAGE_KEY,
    PRIORITY_FILTER_STORAGE_KEY,
    PRIORITIES,
    buildBoardTasks,
    collectBoardProjects,
    filterBoardTasks,
    groupBoardTasks,
    priorityDotColors,
    priorityLabels,
    splitBoardLanes,
    type GroupBy,
    type TaskWithProject,
  } from '../lib/taskBoard';

  interface Props {
    projectsByWeek?: Record<string, ProjectsByStatus>;
    projectsByMonth?: Record<string, ProjectsByStatus>;
    currentWeek?: string;
    currentMonth?: string;
  }

  let { projectsByWeek, projectsByMonth, currentWeek, currentMonth }: Props = $props();

  // ─── Search ───────────────────────────────────────────────────
  let searchQuery = $state('');

  function highlightSegments(text: string, query: string): { text: string; highlight: boolean }[] {
    if (!query.trim()) return [{ text, highlight: false }];
    const lower = text.toLowerCase();
    const q = query.toLowerCase().trim();
    const segments: { text: string; highlight: boolean }[] = [];
    let lastIdx = 0;
    let idx = lower.indexOf(q, lastIdx);
    while (idx !== -1) {
      if (idx > lastIdx) segments.push({ text: text.slice(lastIdx, idx), highlight: false });
      segments.push({ text: text.slice(idx, idx + q.length), highlight: true });
      lastIdx = idx + q.length;
      idx = lower.indexOf(q, lastIdx);
    }
    if (lastIdx < text.length) segments.push({ text: text.slice(lastIdx), highlight: false });
    return segments;
  }

  // ─── Period label for header badge ────────────────────────────
  let periodLabel = $derived.by(() => {
    if (currentWeek) {
      return getWeekDisplayRangeWithYear(currentWeek);
    }
    // Year scale: projectsByMonth has multiple months
    if (projectsByMonth) {
      const monthKeys = Object.keys(projectsByMonth).sort();
      if (monthKeys.length > 1) {
        // Show year (e.g., "2026")
        const year = monthKeys[0].split('-')[0];
        return year;
      }
    }
    if (currentMonth) {
      return getMonthDisplayName(currentMonth);
    }
    return '';
  });

  // ─── Gather all visible projects ──────────────────────────────
  let allProjects = $derived(collectBoardProjects(projectsByWeek, currentWeek, projectsByMonth));

  // Build project lookup
  let projectMap = $derived(new Map(allProjects.map((p) => [p.id, p])));

  // ─── Fetch all project tasks on mount ─────────────────────────
  let tasksByProject = $state<Record<string, Task[]>>({});
  let hasFetched = $state(false);

  // Batch fetch tasks for visible projects
  // eslint-disable-next-line momentium/no-unguarded-effect -- data fetch: batch-loads tasks for all visible projects, triggers once when allProjects changes
  $effect(() => {
    const ids = allProjects.map((p) => p.id);
    if (ids.length > 0) {
      batchFetchProjectTasks(ids).then(() => {
        hasFetched = true;
      });
    } else {
      hasFetched = true;
    }
  });

  // Subscribe to all cached tasks
  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: subscribeToAllCachedTasks pub/sub, returns cleanup
  $effect(() => {
    return subscribeToAllCachedTasks((allTasks) => {
      tasksByProject = allTasks;
    });
  });

  // ─── Aggregate tasks into kanban lanes ────────────────────────
  let allTasks = $derived(buildBoardTasks(allProjects, tasksByProject));

  // Apply priority + project + search filters
  let filteredTasks = $derived.by(() =>
    filterBoardTasks(allTasks, {
      priorities: priorityFilter,
      projectIds: projectFilter,
      query: searchQuery,
    }),
  );

  // Kanban lanes
  let boardLanes = $derived(splitBoardLanes(filteredTasks));
  let todoTasks = $derived(boardLanes.todo);
  let activeTasks = $derived(boardLanes.active);
  let doneTasks = $derived(boardLanes.done);

  // ─── Grouping (persisted) ───────────────────────────────────
  // Persist groupBy to localStorage
  const groupByStore = persistedWritable<GroupBy>(GROUP_BY_STORAGE_KEY, 'none');
  let groupBy = $state<GroupBy>('none');
  // Initialize from store (cleanup handled by $effect)
  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: persistedWritable store sync, returns cleanup
  $effect(() => groupByStore.subscribe((v) => (groupBy = v)));
  function setGroupBy(v: GroupBy) {
    groupBy = v;
    groupByStore.set(v);
  }

  // ─── Priority Filter (persisted) ─────────────────────────────
  const priorityFilterStore = persistedWritable<TaskPriority[]>(PRIORITY_FILTER_STORAGE_KEY, []);
  let priorityFilter = $state<Set<TaskPriority>>(new Set());
  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: persistedWritable store sync, returns cleanup
  $effect(() => priorityFilterStore.subscribe((v) => (priorityFilter = new Set(v))));
  function setPriorityFilter(pf: Set<TaskPriority>) {
    priorityFilter = pf;
    priorityFilterStore.set([...pf]);
  }

  // Priority filter dropdown state
  let priorityDropdownOpen = $state(false);
  let priorityBtnEl: HTMLButtonElement | undefined = $state();
  let priorityPopoverEl: HTMLDivElement | undefined = $state();
  let priorityPopoverStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');

  function togglePriorityFilter(p: TaskPriority) {
    const next = new Set(priorityFilter);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    setPriorityFilter(next);
  }

  // ─── Project Filter (NOT persisted — resets on period change) ─
  let projectFilter = $state<Set<string>>(new Set());
  let projectDropdownOpen = $state(false);
  let projectSearchQuery = $state('');
  let projectBtnEl: HTMLButtonElement | undefined = $state();
  let projectPopoverEl: HTMLDivElement | undefined = $state();
  let projectPopoverStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');

  function toggleProjectFilter(pid: string) {
    const next = new Set(projectFilter);
    if (next.has(pid)) next.delete(pid);
    else next.add(pid);
    projectFilter = next;
  }

  // Clear project filter on period change
  // svelte-ignore state_referenced_locally
  let lastPeriodKey = $state(currentWeek || currentMonth || '');
  // eslint-disable-next-line momentium/no-unguarded-effect -- clear project filter when period changes
  $effect(() => {
    const key = currentWeek || currentMonth || '';
    if (key !== lastPeriodKey) {
      lastPeriodKey = key;
      projectFilter = new Set();
      projectSearchQuery = '';
    }
  });

  // Searchable project list for dropdown (only projects visible in this period)
  let filteredProjectOptions = $derived.by(() => {
    const sorted = [...allProjects].sort((a, b) => a.title.localeCompare(b.title));
    if (!projectSearchQuery.trim()) return sorted;
    const q = projectSearchQuery.toLowerCase().trim();
    return sorted.filter((p) => p.title.toLowerCase().includes(q));
  });

  // Whether any filters are active
  let hasStructuredFilters = $derived(priorityFilter.size > 0 || projectFilter.size > 0);
  let hasActiveFilters = $derived(hasStructuredFilters || searchQuery.trim() !== '');

  function clearAllFilters() {
    setPriorityFilter(new Set());
    projectFilter = new Set();
    projectSearchQuery = '';
    searchQuery = '';
  }

  // ─── Position filter popovers ─────────────────────────────────
  async function positionPopover(
    btnEl: HTMLElement,
    popoverEl: HTMLDivElement,
    setter: (style: string) => void,
  ) {
    const result = await computePosition(btnEl, popoverEl, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [
        offset(4),
        flip({ fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] }),
        shift({ padding: 8 }),
      ],
    });
    setter(`position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`);
  }

  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI positioning for filter popovers
  $effect(() => {
    if (priorityDropdownOpen && priorityBtnEl && priorityPopoverEl) {
      positionPopover(priorityBtnEl, priorityPopoverEl, (s) => (priorityPopoverStyle = s));
    }
    if (projectDropdownOpen && projectBtnEl && projectPopoverEl) {
      positionPopover(projectBtnEl, projectPopoverEl, (s) => (projectPopoverStyle = s));
    }
  });

  // Close dropdowns on outside click
  function handlePageClick(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (priorityDropdownOpen) {
      if (!priorityBtnEl?.contains(target) && !priorityPopoverEl?.contains(target)) {
        priorityDropdownOpen = false;
      }
    }
    if (projectDropdownOpen) {
      if (!projectBtnEl?.contains(target) && !projectPopoverEl?.contains(target)) {
        projectDropdownOpen = false;
      }
    }
  }

  let todoGrouped = $derived(groupBoardTasks(todoTasks, groupBy));
  let activeGrouped = $derived(groupBoardTasks(activeTasks, groupBy));
  let doneGrouped = $derived(groupBoardTasks(doneTasks, groupBy));

  // ─── Priority colors ─────────────────────────────────────────
  const categoryChipColors: Record<ProjectCategory, { bg: string; text: string; border: string }> =
    {
      tech: { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' },
      health: { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' },
      personal: { bg: '#fce7f3', text: '#9d174d', border: '#fbcfe8' },
      finance: { bg: '#fef9c3', text: '#854d0e', border: '#fde68a' },
      travel: { bg: '#e0e7ff', text: '#3730a3', border: '#c7d2fe' },
      other: { bg: '#f3f4f6', text: '#374151', border: '#e5e7eb' },
    };

  // ─── Actions ──────────────────────────────────────────────────
  async function handleComplete(task: TaskWithProject) {
    const projectTasks = tasksByProject[task.projectId] || [];
    await completeTask(task.projectId, task.id, projectTasks);
  }

  async function handleToggleActive(task: TaskWithProject, active?: boolean) {
    const projectTasks = tasksByProject[task.projectId] || [];
    await toggleTaskActive(task.projectId, task.id, projectTasks, active);
  }

  async function handleAddTask({
    projectId,
    title,
    notes,
    priority,
    tagNames,
  }: NewTaskDraft & { projectId: string }) {
    await addTask(projectId, title, tasksByProject[projectId] || [], { notes, priority, tagNames });
  }

  function handleProjectClick(projectId: string) {
    openProjectRail(projectId);
  }

  // ─── Task Detail Popover ──────────────────────────────────────
  let popoverTask = $state<Task | null>(null);
  let popoverAnchorEl = $state<HTMLElement | null>(null);

  function openTaskPopover(task: TaskWithProject, anchorEl: HTMLElement) {
    if (popoverTask?.id === task.id) {
      closeTaskPopover();
      return;
    }
    popoverTask = task;
    popoverAnchorEl = anchorEl;
  }

  function closeTaskPopover() {
    popoverTask = null;
    popoverAnchorEl = null;
  }

  async function handlePopoverUpdate(task: Task, updates: Partial<Task>) {
    const projectId = task.projectId;
    const projectTasks = tasksByProject[projectId] || [];
    const prevTasks = [...projectTasks];

    // Optimistic update
    const newTasks = projectTasks.map((t) => (t.id === task.id ? { ...t, ...updates } : t));
    setProjectTasks(projectId, newTasks);

    try {
      const response = await fetch(`/projects/${projectId}/tasks/${task.id}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ task: updates }),
      });
      if (response.ok) {
        const updated: Task = await response.json();
        const finalTasks = (tasksByProject[projectId] || []).map((t) =>
          t.id === task.id ? updated : t,
        );
        setProjectTasks(projectId, finalTasks);
        if (popoverTask?.id === task.id) {
          popoverTask = updated;
        }
      } else {
        setProjectTasks(projectId, prevTasks);
      }
    } catch {
      setProjectTasks(projectId, prevTasks);
    }
  }

  // The task sheet's "Working on" switch (phones): keeps the open sheet in step with the pin.
  async function handlePopoverToggleActive(task: Task) {
    const next = !task.isActive;
    if (popoverTask?.id === task.id) popoverTask = { ...popoverTask, isActive: next };
    const result = await toggleTaskActive(
      task.projectId,
      task.id,
      tasksByProject[task.projectId] || [],
      next,
    );
    if (!result.success && popoverTask?.id === task.id) {
      popoverTask = { ...popoverTask, isActive: !next };
    }
  }

  async function handlePopoverDelete(task: Task) {
    closeTaskPopover();
    const projectTasks = tasksByProject[task.projectId] || [];
    await deleteTaskApi(task.projectId, task.id, projectTasks);
  }

  async function handlePopoverMarkWontDo(task: Task, pendingUpdates?: Partial<Task>) {
    closeTaskPopover();
    const projectTasks = tasksByProject[task.projectId] || [];
    if (pendingUpdates) {
      // Apply pending title/notes edits first
      const merged = projectTasks.map((t) => (t.id === task.id ? { ...t, ...pendingUpdates } : t));
      setProjectTasks(task.projectId, merged);
    }
    const currentTasks = tasksByProject[task.projectId] || [];
    await markTaskWontDo(task.projectId, task.id, currentTasks, pendingUpdates);
  }

  async function handlePopoverToggleComplete(task: Task, pendingUpdates?: Partial<Task>) {
    closeTaskPopover();
    const projectTasks = tasksByProject[task.projectId] || [];
    if (pendingUpdates) {
      const merged = projectTasks.map((t) => (t.id === task.id ? { ...t, ...pendingUpdates } : t));
      setProjectTasks(task.projectId, merged);
      // Save pending edits
      await handlePopoverUpdate(task, pendingUpdates);
    }
    const currentTasks = tasksByProject[task.projectId] || [];
    await completeTask(task.projectId, task.id, currentTasks);
  }

  // ─── Drag and drop ────────────────────────────────────────────
  let draggedTask = $state<TaskWithProject | null>(null);
  let dropLane = $state<string | null>(null);

  function handleDragStart(e: DragEvent, task: TaskWithProject) {
    draggedTask = task;
    e.dataTransfer!.effectAllowed = 'move';
    e.dataTransfer!.setData('text/plain', task.id);
  }

  function handleDragEnd() {
    draggedTask = null;
    dropLane = null;
  }

  function handleDragOver(e: DragEvent, lane: string) {
    e.preventDefault();
    dropLane = lane;
  }

  function handleDragLeave() {
    dropLane = null;
  }

  async function handleDrop(e: DragEvent, lane: string) {
    e.preventDefault();
    dropLane = null;
    if (!draggedTask) return;

    const task = draggedTask;
    draggedTask = null;

    if (lane === 'done' && task.status === 'todo') {
      // Complete the task (from To Do or Working On)
      await handleComplete(task);
    } else if (lane === 'active' && task.status === 'todo' && !task.isActive) {
      // Move to "Working On" from To Do
      await handleToggleActive(task, true);
    } else if (lane === 'active' && (task.status === 'done' || task.status === 'wont_do')) {
      // Reopen + pin: from Done → Working On (two-step)
      const projectTasks = tasksByProject[task.projectId] || [];
      const result = await completeTask(task.projectId, task.id, projectTasks);
      await toggleTaskActive(task.projectId, task.id, result.tasks, true);
    } else if (lane === 'todo' && task.isActive) {
      // Move back to "To Do" from Working On
      await handleToggleActive(task, false);
    } else if (lane === 'todo' && (task.status === 'done' || task.status === 'wont_do')) {
      // Reopen task from Done → To Do
      await handleComplete(task);
    }
  }

  // ─── Lane config ──────────────────────────────────────────────
  const lanes = [
    { key: 'todo', label: 'To Do', icon: '📋', accent: '#3b82f6' },
    { key: 'active', label: 'Working On', icon: '📌', accent: '#f59e0b' },
    { key: 'done', label: 'Done', icon: '✅', accent: '#22c55e' },
  ] as const;

  let mobileGroups = $derived({
    todo: todoGrouped,
    active: activeGrouped,
    done: doneGrouped,
  });
  let mobileCounts = $derived({
    todo: todoTasks.length,
    active: activeTasks.length,
    done: doneTasks.length,
  });

  function getLaneTasks(key: string) {
    switch (key) {
      case 'todo':
        return todoGrouped;
      case 'active':
        return activeGrouped;
      case 'done':
        return doneGrouped;
      default:
        return [];
    }
  }

  function getLaneCount(key: string) {
    switch (key) {
      case 'todo':
        return todoTasks.length;
      case 'active':
        return activeTasks.length;
      case 'done':
        return doneTasks.length;
      default:
        return 0;
    }
  }
</script>

{#if $viewport.isMobile}
  <!-- Phones: status segments + grouped list (state and data stay here, shared with desktop) -->
  {#if !hasFetched}
    <div class="task-kanban-container">
      <div class="loading-state">
        <span class="loading-spinner">⏳</span>
        <span>Loading tasks…</span>
      </div>
    </div>
  {:else}
    <MobileTaskList
      groups={mobileGroups}
      counts={mobileCounts}
      projects={allProjects}
      bind:searchQuery
      {groupBy}
      {priorityFilter}
      {projectFilter}
      onGroupByChange={setGroupBy}
      onPriorityFilterChange={setPriorityFilter}
      onProjectFilterChange={(ids) => (projectFilter = ids)}
      onClearFilters={clearAllFilters}
      onOpenTask={openTaskPopover}
      onComplete={handleComplete}
      onTogglePin={(task) => handleToggleActive(task)}
      onAddTask={handleAddTask}
    />
  {/if}
{:else}
  <div class="task-kanban-container" onclick={handlePageClick} role="presentation">
    <!-- Header bar -->
    <header class="kanban-header">
      <div class="header-left">
        <h1 class="view-title">✅ Task Board</h1>
        <span class="period-badge">
          <span class="period-label">{periodLabel}</span>
          <span class="period-separator">·</span>
          <span class="period-stats"
            >{hasActiveFilters ? `${filteredTasks.length} of ${allTasks.length}` : allTasks.length} tasks
            across {allProjects.length} projects</span
          >
        </span>
      </div>
      <div class="header-right">
        <!-- Search -->
        <div class="search-wrapper">
          <svg class="search-icon" width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <path
              d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242.156a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z"
            />
          </svg>
          <input
            autocomplete="off"
            type="text"
            class="search-input"
            placeholder="Search tasks…"
            bind:value={searchQuery}
            onkeydown={(e) => {
              if (e.key === 'Escape') searchQuery = '';
            }}
          />
          <button
            type="button"
            class="search-clear"
            class:visible={searchQuery !== ''}
            onclick={() => (searchQuery = '')}
            aria-label="Clear search"
            disabled={searchQuery === ''}
          >
            ✕
          </button>
        </div>

        <span class="header-divider"></span>

        <!-- Group By tabs -->
        <div class="filter-section">
          <span class="filter-section-label">Group by</span>
          <div class="group-tabs" role="tablist" aria-label="Group tasks by">
            {#each [{ value: 'none', label: 'All' }, { value: 'priority', label: 'Priority' }, { value: 'project', label: 'Project' }] as tab}
              <button
                type="button"
                role="tab"
                class="group-tab"
                class:active={groupBy === tab.value}
                aria-selected={groupBy === tab.value}
                onclick={() => setGroupBy(tab.value as GroupBy)}
              >
                {tab.label}
              </button>
            {/each}
          </div>
        </div>

        <span class="header-divider"></span>

        <!-- Priority filter dropdown -->
        <div class="filter-dropdown-wrapper">
          <button
            bind:this={priorityBtnEl}
            class="filter-btn"
            class:filter-active={priorityFilter.size > 0}
            onclick={(e) => {
              e.stopPropagation();
              priorityDropdownOpen = !priorityDropdownOpen;
              projectDropdownOpen = false;
            }}
          >
            {priorityFilter.size === 0 ? 'All priorities' : `Priority (${priorityFilter.size})`}
            <span class="filter-chevron">{priorityDropdownOpen ? '▲' : '▼'}</span>
          </button>
          {#if priorityDropdownOpen}
            <div bind:this={priorityPopoverEl} class="filter-popover" style={priorityPopoverStyle}>
              {#each PRIORITIES as p}
                <label class="filter-option">
                  <input
                    type="checkbox"
                    checked={priorityFilter.has(p)}
                    onchange={() => togglePriorityFilter(p)}
                  />
                  <span class="filter-option-label">
                    <span class="priority-dot-inline" style="background: {priorityDotColors[p]};"
                    ></span>
                    {priorityLabels[p]}
                  </span>
                </label>
              {/each}
              {#if priorityFilter.size > 0}
                <div class="filter-popover-divider"></div>
                <button class="filter-clear-btn" onclick={() => setPriorityFilter(new Set())}
                  >Clear all</button
                >
              {/if}
            </div>
          {/if}
        </div>

        <!-- Project filter dropdown (multiselect, searchable) -->
        <div class="filter-dropdown-wrapper">
          <button
            bind:this={projectBtnEl}
            class="filter-btn"
            class:filter-active={projectFilter.size > 0}
            onclick={(e) => {
              e.stopPropagation();
              projectDropdownOpen = !projectDropdownOpen;
              priorityDropdownOpen = false;
            }}
          >
            {projectFilter.size === 0 ? 'All projects' : `Projects (${projectFilter.size})`}
            <span class="filter-chevron">{projectDropdownOpen ? '▲' : '▼'}</span>
          </button>
          {#if projectDropdownOpen}
            <div
              bind:this={projectPopoverEl}
              class="filter-popover project-filter-popover"
              style={projectPopoverStyle}
            >
              <div class="filter-search-wrapper">
                <input
                  autocomplete="off"
                  type="text"
                  class="filter-search-input"
                  placeholder="Search projects…"
                  bind:value={projectSearchQuery}
                />
              </div>
              <div class="filter-options-scroll">
                {#each filteredProjectOptions as proj (proj.id)}
                  <label class="filter-option">
                    <input
                      type="checkbox"
                      checked={projectFilter.has(proj.id)}
                      onchange={() => toggleProjectFilter(proj.id)}
                    />
                    <span class="filter-option-label">
                      {categoryIcons[proj.category] || '📋'}
                      {proj.title}
                    </span>
                  </label>
                {:else}
                  <div class="filter-empty">No projects match</div>
                {/each}
              </div>
              {#if projectFilter.size > 0}
                <div class="filter-popover-divider"></div>
                <button
                  class="filter-clear-btn"
                  onclick={() => {
                    projectFilter = new Set();
                    projectSearchQuery = '';
                  }}>Clear all</button
                >
              {/if}
            </div>
          {/if}
        </div>

        <!-- Clear all filters icon -->
        {#if hasStructuredFilters}
          <button
            type="button"
            class="clear-filters-btn"
            onclick={clearAllFilters}
            title="Clear all filters"
          >
            ✕
          </button>
        {/if}
      </div>
    </header>

    {#if !hasFetched}
      <div class="loading-state">
        <span class="loading-spinner">⏳</span>
        <span>Loading tasks…</span>
      </div>
    {:else}
      <!-- Kanban Board -->
      <div class="kanban-board">
        {#each lanes as lane}
          {@const groups = getLaneTasks(lane.key)}
          {@const count = getLaneCount(lane.key)}

          <div
            class="kanban-lane"
            class:drop-target={dropLane === lane.key}
            style="--lane-accent: {lane.accent};"
            role="group"
            aria-label="{lane.label} tasks"
            ondragover={(e) => handleDragOver(e, lane.key)}
            ondragleave={handleDragLeave}
            ondrop={(e) => handleDrop(e, lane.key)}
          >
            <div class="lane-header">
              <span class="lane-icon">{lane.icon}</span>
              <span class="lane-title">{lane.label}</span>
              <span class="lane-count">{count}</span>
            </div>

            <div
              class="lane-content"
              class:lane-grid={lane.key === 'todo' || lane.key === 'active'}
            >
              {#each groups as group (group.key)}
                {#if group.label}
                  <div class="group-header">
                    <div class="group-header-main">
                      {#if group.priorityKey}
                        <span
                          class="priority-dot-inline"
                          style="background: {priorityDotColors[group.priorityKey]};"
                        ></span>
                      {/if}
                      <span class="group-header-label">{group.label}</span>
                    </div>
                    {#if groupBy === 'project' && group.projectId}
                      <Link
                        class="group-project-link"
                        href="/projects/{group.projectId}?tasks_view=board"
                        title="Open project board"
                        aria-label="Open project board">↗</Link
                      >
                    {/if}
                  </div>
                {/if}

                {#each group.tasks as task (task.id)}
                  {@const chipColor =
                    categoryChipColors[task.project.category] || categoryChipColors.other}

                  <!-- svelte-ignore a11y_no_static_element_interactions -->
                  <div
                    class="task-card"
                    class:is-dragging={draggedTask?.id === task.id}
                    class:is-done={task.status === 'done' || task.status === 'wont_do'}
                    class:is-wont-do={task.status === 'wont_do'}
                    role="listitem"
                    draggable="true"
                    ondragstart={(e) => handleDragStart(e, task)}
                    ondragend={handleDragEnd}
                  >
                    <div class="card-top-row">
                      <!-- Checkbox -->
                      <button
                        type="button"
                        class="task-checkbox"
                        class:checked={task.status !== 'todo'}
                        class:wont-do={task.status === 'wont_do'}
                        style="border-color: {task.status === 'todo'
                          ? priorityDotColors[task.priority]
                          : '#22c55e'};"
                        onclick={() => handleComplete(task)}
                        title={task.status === 'todo' ? 'Complete task' : 'Reopen task'}
                      >
                        {#if task.status === 'wont_do'}
                          ✕
                        {:else if task.status === 'done'}
                          ✓
                        {/if}
                      </button>

                      <!-- Title (clickable to open popover) -->
                      <span
                        class="task-title"
                        title={task.title}
                        role="button"
                        tabindex="0"
                        onclick={(e) => openTaskPopover(task, e.currentTarget as HTMLElement)}
                        onkeydown={(e) => {
                          if (e.key === 'Enter')
                            openTaskPopover(task, e.currentTarget as HTMLElement);
                        }}
                      >
                        {#each highlightSegments(task.title, searchQuery) as seg, i (i)}
                          {#if seg.highlight}
                            <mark class="search-highlight">{seg.text}</mark>
                          {:else}
                            {seg.text}
                          {/if}
                        {/each}</span
                      >

                      <!-- Priority dot -->
                      {#if task.priority !== 'none'}
                        <span
                          class="priority-dot"
                          style="background: {priorityDotColors[task.priority]};"
                          title="{task.priority} priority"
                        ></span>
                      {/if}

                      <!-- Active pin toggle (only in todo lane) -->
                      {#if task.status === 'todo'}
                        <button
                          type="button"
                          class="pin-btn"
                          class:pinned={task.isActive}
                          onclick={() => handleToggleActive(task)}
                          title={task.isActive ? 'Unpin from Working On' : 'Pin to Working On'}
                        >
                          📌
                        </button>
                      {/if}
                    </div>

                    <div class="card-bottom-row">
                      <!-- Project chip -->
                      <button
                        type="button"
                        class="project-chip"
                        style="background: {chipColor.bg}; color: {chipColor.text}; border-color: {chipColor.border};"
                        onclick={() => handleProjectClick(task.projectId)}
                        title="Open {task.project.title}"
                      >
                        {categoryIcons[task.project.category] || '📋'}
                        <span class="chip-title">{task.project.title}</span>
                      </button>

                      {#if task.notes}
                        <span class="has-notes" title="Has notes">📝</span>
                      {/if}
                    </div>
                  </div>
                {/each}
              {/each}

              {#if count === 0}
                <div class="lane-empty">
                  <span class="empty-icon">{lane.icon}</span>
                  <span class="empty-text">
                    {#if lane.key === 'active'}
                      Drag tasks here or click 📌 to pin
                    {:else if lane.key === 'done'}
                      No completed tasks yet
                    {:else}
                      No tasks
                    {/if}
                  </span>
                </div>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>
{/if}

<!-- Task detail popover (shared instance) -->
<TaskDetailPopover
  task={popoverTask}
  anchorEl={popoverAnchorEl}
  project={popoverTask ? (projectMap.get(popoverTask.projectId) ?? null) : null}
  onClose={closeTaskPopover}
  onUpdate={handlePopoverUpdate}
  onDelete={(task, pendingUpdates) => {
    if (pendingUpdates) handlePopoverUpdate(task, pendingUpdates);
    handlePopoverDelete(task);
  }}
  onMarkWontDo={handlePopoverMarkWontDo}
  onToggleComplete={handlePopoverToggleComplete}
  onToggleActive={$viewport.isMobile ? handlePopoverToggleActive : undefined}
/>

<style>
  .task-kanban-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0.75rem;
    gap: 0.75rem;
    overflow: hidden;
    background: #f8fafc;
  }

  /* ─── Header ─── */
  .kanban-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    flex-shrink: 0;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .view-title {
    font-size: 0.875rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
  }

  .period-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.1875rem 0.625rem;
    background: #eef2ff;
    border: 1px solid #c7d2fe;
    border-radius: 1rem;
    font-size: 0.6875rem;
  }

  .period-label {
    font-weight: 600;
    color: #4338ca;
  }

  .period-separator {
    color: #a5b4fc;
  }

  .period-stats {
    color: #6366f1;
    font-weight: 500;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .filter-section {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .filter-section-label {
    font-size: 0.625rem;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  .header-divider {
    width: 1px;
    height: 1.25rem;
    background: #e2e8f0;
    flex-shrink: 0;
  }

  /* ─── Search ─── */
  .search-wrapper {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.375rem;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    transition: border-color 0.15s;
  }

  .search-wrapper:focus-within {
    border-color: #2563eb;
  }

  .search-icon {
    color: #9ca3af;
    flex-shrink: 0;
  }

  .search-wrapper:focus-within .search-icon {
    color: #60a5fa;
  }

  .search-input {
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.6875rem;
    color: #374151;
    width: 10rem;
    padding: 0;
    -webkit-appearance: none;
    appearance: none;
  }

  .search-input:focus,
  .search-input:focus-visible {
    outline: none;
    box-shadow: none;
  }

  .search-input::placeholder {
    color: #9ca3af;
  }

  .search-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 0.875rem;
    height: 0.875rem;
    font-size: 0.5rem;
    color: #9ca3af;
    background: #f1f5f9;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;
    opacity: 0;
    pointer-events: none;
    transition: all 0.1s;
  }

  .search-clear.visible {
    opacity: 1;
    pointer-events: auto;
  }

  .search-clear:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: 1px;
  }

  .search-clear:hover {
    color: #374151;
    background: #e2e8f0;
  }

  /* ─── Filter Dropdowns ─── */
  .filter-dropdown-wrapper {
    position: relative;
  }

  .filter-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #374151;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }

  .filter-btn:hover {
    border-color: #9ca3af;
    background: #f9fafb;
  }

  .filter-btn.filter-active {
    color: #2563eb;
    border-color: #93c5fd;
    background: #eff6ff;
  }

  .filter-chevron {
    font-size: 0.5rem;
    color: #9ca3af;
  }

  .filter-popover {
    z-index: 50;
    min-width: 10rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    padding: 0.25rem 0;
  }

  .project-filter-popover {
    min-width: 14rem;
    max-width: 18rem;
  }

  .filter-search-wrapper {
    padding: 0.375rem 0.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .filter-search-input {
    width: 100%;
    padding: 0.25rem 0.375rem;
    font-size: 0.6875rem;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    outline: none;
    transition: border-color 0.15s;
  }

  .filter-search-input:focus {
    border-color: #3b82f6;
  }

  .filter-options-scroll {
    max-height: 12rem;
    overflow-y: auto;
    scrollbar-width: thin;
  }

  .filter-option {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.625rem;
    font-size: 0.6875rem;
    cursor: pointer;
    transition: background 0.1s;
  }

  .filter-option:hover {
    background: #f3f4f6;
  }

  .filter-option input[type='checkbox'] {
    width: 0.8125rem;
    height: 0.8125rem;
    accent-color: #3b82f6;
    margin: 0;
    cursor: pointer;
    flex-shrink: 0;
  }

  .filter-option-label {
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .filter-popover-divider {
    height: 1px;
    background: #e5e7eb;
    margin: 0.25rem 0;
  }

  .filter-clear-btn {
    display: block;
    width: 100%;
    padding: 0.375rem 0.625rem;
    font-size: 0.6875rem;
    color: #6b7280;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: all 0.1s;
  }

  .filter-clear-btn:hover {
    color: #1f2937;
    background: #f3f4f6;
  }

  .priority-dot-inline {
    display: inline-block;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    flex-shrink: 0;
    vertical-align: middle;
  }

  .filter-empty {
    padding: 0.75rem 0.625rem;
    font-size: 0.6875rem;
    color: #94a3b8;
    text-align: center;
  }

  .clear-filters-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    font-size: 0.625rem;
    font-weight: 700;
    color: #94a3b8;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.12s;
    flex-shrink: 0;
  }

  .clear-filters-btn:hover {
    color: #ef4444;
    background: #fef2f2;
    border-color: #fecaca;
  }

  /* ─── Group Tabs ─── */
  .group-tabs {
    display: flex;
    background: #f1f5f9;
    border-radius: 0.375rem;
    padding: 0.125rem;
    gap: 0.0625rem;
  }

  .group-tab {
    padding: 0.25rem 0.625rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #64748b;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.12s;
    white-space: nowrap;
  }

  .group-tab:hover {
    color: #334155;
    background: rgba(255, 255, 255, 0.5);
  }

  .group-tab.active {
    color: #1e293b;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    font-weight: 600;
  }

  /* ─── Loading ─── */
  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    flex: 1;
    color: #94a3b8;
    font-size: 0.875rem;
  }

  .loading-spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* ─── Kanban Board ─── */
  .kanban-board {
    display: flex;
    gap: 0.75rem;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  /* ─── Lane ─── */
  .kanban-lane {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;
    overflow: hidden;
    min-width: 0;
    min-height: 0;
    position: relative;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: all 0.15s;
  }

  /* Accent bar at top */
  .kanban-lane::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--lane-accent);
  }

  .kanban-lane.drop-target {
    background: #eff6ff !important;
    outline: 2px solid #3b82f6;
    outline-offset: -2px;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15) !important;
  }

  /* To Do: 2/5, Working On: 2/5, Done: 1/5 */
  .kanban-lane:first-child,
  .kanban-lane:nth-child(2) {
    flex: 2;
  }

  .kanban-lane:last-child {
    flex: 1;
  }

  .lane-header {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.625rem;
    padding-top: 0.625rem;
    border-bottom: 1px solid #f1f5f9;
    flex-shrink: 0;
  }

  .lane-icon {
    font-size: 0.8125rem;
  }

  .lane-title {
    font-size: 0.75rem;
    font-weight: 700;
    color: #334155;
  }

  .lane-count {
    font-size: 0.625rem;
    font-weight: 600;
    color: #64748b;
    background: #f1f5f9;
    padding: 0.0625rem 0.375rem;
    border-radius: 1rem;
    min-width: 1.25rem;
    text-align: center;
  }

  .lane-content {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding: 0.5rem;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }

  .lane-content::-webkit-scrollbar {
    width: 5px;
  }

  .lane-content::-webkit-scrollbar-track {
    background: transparent;
  }

  .lane-content::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  /* ─── Two-column grid for To Do / Working On ─── */
  .lane-content.lane-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    align-content: start;
    --title-lines: 2;
  }

  .lane-content.lane-grid .group-header {
    grid-column: 1 / -1;
  }

  .lane-content.lane-grid .lane-empty {
    grid-column: 1 / -1;
  }

  /* Single-column lanes (done) get 3 lines */
  .lane-content:not(.lane-grid) {
    --title-lines: 3;
  }

  @media (max-width: 1200px) {
    .lane-content.lane-grid {
      grid-template-columns: 1fr;
      --title-lines: 3;
    }
  }

  /* ─── Group Header ─── */
  .group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.375rem;
    font-size: 0.625rem;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.375rem 0.25rem 0.125rem;
    border-bottom: 1px solid #f1f5f9;
    margin-top: 0.25rem;
  }

  .group-header-main {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
  }

  .group-header-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :global(.group-project-link) {
    color: #94a3b8;
    text-decoration: none;
    font-size: 0.6875rem;
    line-height: 1;
    padding: 0.0625rem;
    border-radius: 0.1875rem;
    flex-shrink: 0;
  }

  :global(.group-project-link:hover) {
    color: #3b82f6;
    background: #eff6ff;
  }

  .group-header:first-child {
    margin-top: 0;
  }

  /* ─── Task Card ─── */
  .task-card {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.5rem 0.625rem;
    background: #fafbfc;
    border: 1px solid #e8ecf0;
    border-radius: 0.375rem;
    cursor: grab;
    transition: all 0.12s ease;
    min-width: 0;
  }

  .task-card:hover {
    background: white;
    border-color: #cbd5e1;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }

  .task-card:active {
    cursor: grabbing;
  }

  .task-card.is-dragging {
    opacity: 0.4;
    transform: scale(0.97);
  }

  .task-card.is-done {
    opacity: 0.65;
  }

  .task-card.is-done .task-title {
    text-decoration: line-through;
    color: #94a3b8;
  }

  .task-card.is-wont-do .task-title {
    text-decoration: line-through;
    color: #cbd5e1;
  }

  /* ─── Card Top Row ─── */
  .card-top-row {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    min-width: 0;
  }

  .task-checkbox {
    width: 1rem;
    height: 1rem;
    min-width: 1rem;
    border-radius: 0.1875rem;
    border: 2px solid #d1d5db;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.5625rem;
    font-weight: 700;
    color: white;
    transition: all 0.12s;
    padding: 0;
  }

  .task-checkbox:hover {
    transform: scale(1.1);
  }

  .task-checkbox.checked {
    background: #22c55e;
    border-color: #22c55e;
  }

  .task-checkbox.wont-do {
    background: #94a3b8;
    border-color: #94a3b8;
  }

  .task-title {
    flex: 1;
    font-size: 0.75rem;
    font-weight: 500;
    color: #334155;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--title-lines, 2);
    line-clamp: var(--title-lines, 2);
    line-height: 1.4;
    cursor: pointer;
    border-radius: 0.125rem;
    padding: 0.0625rem 0.125rem;
    margin: -0.0625rem -0.125rem;
    transition: background-color 0.1s;
    word-break: break-word;
  }

  .task-title:hover {
    background: rgba(59, 130, 246, 0.06);
    color: #1e40af;
  }

  :global(.search-highlight) {
    background: #fef08a;
    color: #854d0e;
    border-radius: 0.125rem;
    padding: 0 0.0625rem;
  }

  .priority-dot {
    width: 0.4375rem;
    height: 0.4375rem;
    min-width: 0.4375rem;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .pin-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 0.6875rem;
    padding: 0.125rem;
    opacity: 0;
    transition: opacity 0.12s;
    flex-shrink: 0;
  }

  .task-card:hover .pin-btn {
    opacity: 0.5;
  }

  .task-card:hover .pin-btn:hover {
    opacity: 1;
  }

  .pin-btn.pinned {
    opacity: 1 !important;
  }

  /* Touch: no hover to reveal the pin, so unpinned pins stay visible but dim */
  @media (hover: none) and (pointer: coarse) {
    .pin-btn {
      opacity: 0.4;
    }
  }

  /* ─── Card Bottom Row ─── */
  .card-bottom-row {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding-left: 1.375rem; /* align with title after checkbox */
  }

  .project-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.0625rem 0.375rem;
    border-radius: 0.1875rem;
    border: 1px solid;
    font-size: 0.5625rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.12s;
    max-width: 10rem;
    overflow: hidden;
  }

  .project-chip:hover {
    filter: brightness(0.95);
  }

  .chip-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .has-notes {
    font-size: 0.5625rem;
    opacity: 0.5;
  }

  /* ─── Empty State ─── */
  .lane-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 2rem 1rem;
    text-align: center;
    color: #94a3b8;
    border: 1.5px dashed #e2e8f0;
    border-radius: 0.375rem;
    background: #fafbfc;
  }

  .empty-icon {
    font-size: 1.5rem;
    opacity: 0.4;
  }

  .empty-text {
    font-size: 0.6875rem;
    font-weight: 500;
  }

  /* ─── Responsive ─── */
  @media (max-width: 1024px) {
    .kanban-lane:first-child,
    .kanban-lane:last-child {
      flex: 1;
    }

    .kanban-header {
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .header-right {
      flex-wrap: wrap;
    }
  }
</style>
